import { getLogger } from '@logtape/logtape'
import { SpanStatusCode, trace } from '@opentelemetry/api'

import { getWebTracer } from './browser-otel-tracer'

const logger = getLogger(['web', 'client-error'])

interface CaptureContext {
  /** 에러 발생 위치 식별자 (예: 'error-boundary:home', 'window.onerror'). */
  source?: string
  [key: string]: unknown
}

const toError = (error: unknown): Error => (error instanceof Error ? error : new Error(String(error)))

/**
 * 에러를 logtape(→Loki)와 OTel 트레이스(→Tempo)에 함께 기록한다.
 * 활성 span이 있으면 그 span에 exception을 남기고, 없으면 짧은 span을 만들어
 * Tempo에 exception을 남긴다. 어느 경우든 로그는 그 span의 trace context 안에서
 * emit되므로 LogRecord에 trace_id가 자동으로 붙어 Loki↔Tempo가 연결된다.
 */
function recordErrorWithTrace(error: unknown, context?: CaptureContext): void {
  const err = toError(error)
  const activeSpan = trace.getActiveSpan()

  if (activeSpan) {
    activeSpan.recordException(err)
    activeSpan.setStatus({ code: SpanStatusCode.ERROR })
    logger.error('client-exception', { error, ...context })
    return
  }

  getWebTracer().startActiveSpan('client-exception', (span) => {
    if (context?.source !== undefined) {
      span.setAttribute('error.source', context.source)
    }
    span.recordException(err)
    span.setStatus({ code: SpanStatusCode.ERROR })
    // 이 콜백은 위 span이 활성인 컨텍스트에서 실행되므로 로그에 trace_id가 주입된다.
    logger.error('client-exception', { error, ...context })
    span.end()
  })
}

/**
 * 클라이언트 에러를 logtape(→Loki)·트레이스(→Tempo)로 보낸다.
 * 에러가 trace_id로 연결되어 Loki↔Tempo에 함께 쌓인다.
 */
export function captureClientException(error: unknown, context?: CaptureContext): void {
  recordErrorWithTrace(error, context)
}

let handlersRegistered = false

/**
 * 잡히지 않은 에러(window.onerror)와 unhandled promise rejection을 trace+logtape로 보낸다.
 * (logtape의 console sink가 console.error를 호출하므로 console.error 후킹은 무한루프
 *  위험이 있어 의도적으로 제외한다.)
 */
export function registerBrowserErrorHandlers(): void {
  if (typeof window === 'undefined' || handlersRegistered) {
    return
  }
  handlersRegistered = true

  window.addEventListener('error', (event) => {
    recordErrorWithTrace(event.error ?? new Error(String(event.message)), {
      source: 'window.onerror',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    recordErrorWithTrace(event.reason, {
      source: 'unhandledrejection',
    })
  })
}
